/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {reInitializeState} from '@ion/applications/src/helpers';
import {Role, User} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {PageState} from '@/types/pages';
import {AdminAction, AsyncState} from '@/types/redux';
import {
  ADD_ADMIN_USER,
  ADD_ADMIN_USER_ERROR,
  ADD_ADMIN_USER_REQUEST,
  ADD_ADMIN_USER_RESPONSE,
  ADMIN_USERS_GRID_REFRESHED,
  ADMIN_USERS_LOAD_ROLES,
  AUDIT_ADMIN_USER,
  AUDIT_ADMIN_USER_ERROR,
  AUDIT_ADMIN_USER_REQUEST,
  AUDIT_ADMIN_USER_RESPONSE,
  DELETE_ADMIN_USER,
  DELETE_ADMIN_USER_ERROR,
  DELETE_ADMIN_USER_REQUEST,
  DELETE_ADMIN_USER_RESPONSE,
  DELETE_ADMIN_USERS,
  DELETE_ADMIN_USERS_ERROR,
  DELETE_ADMIN_USERS_REQUEST,
  DELETE_ADMIN_USERS_RESPONSE,
  EDIT_ADMIN_USER,
  EDIT_ADMIN_USER_ERROR,
  EDIT_ADMIN_USER_REQUEST,
  EDIT_ADMIN_USER_RESPONSE,
  EDIT_ADMIN_USERS,
  EDIT_ADMIN_USERS_ERROR,
  EDIT_ADMIN_USERS_REQUEST,
  EDIT_ADMIN_USERS_RESPONSE,
  GET_ADMIN_USERS_ERROR,
  GET_ADMIN_USERS_REQUEST,
  GET_ADMIN_USERS_RESPONSE,
  IMPORT_ADMIN_USERS,
  INITIALIZE_ADMIN_USERS,
  PRE_INITIALIZE_ADMIN_USERS,
  RE_INITIALIZE_ADMIN_USERS,
  SET_ADMIN_USERS_GRID_FILTER_MODEL,
  SET_GRID_COLUMNS,
  UPDATE_GRID_COLUMNS
} from '../actions/admin-users';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '../helpers';

export interface AdminUsersAction extends AdminAction {
  user?: User;
  users?: User[];
};

export interface AdminUsersEditAction extends AdminAction {
  user?: User;
  users?: User[];
  isCurrentUser: boolean;
};

export interface AdminUsersActionState extends AsyncState {
  selected: User | User[] | null;
  result: User | User[] | null;
  isEditing: boolean;
  record?: User | User[] | null;
};

export interface AdminUsersImportState {
  isImporting: boolean;
  result: User | User[] | null;
};

export interface AdminUsersEditActionState extends AdminUsersActionState {
  isCurrentUser: boolean;
}

export interface AdminUsersState extends PageState {
  isPreInitialized: boolean;
  users: {
    error: ServiceError;
    loading: boolean;
  },
  roles: Role[],
  loadRolesPending: boolean,
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    filterModel: FilterModel | {}
  },
  add: AdminUsersActionState,
  edit: AdminUsersEditActionState,
  audit: AdminUsersActionState,
  destroy: AdminUsersActionState,
  bulkEdit: AdminUsersActionState,
  bulkDelete: AdminUsersActionState,
  bulkImport: AdminUsersImportState
}

// defines the user preference key for the admin users page ...
const PREFERENCE = 'admin-users';

// defines the initial state for the reducer ...
export const initialState: AdminUsersState = {
  isPreInitialized : false,
  isInitialized    : false,
  users            : {
    error   : null,
    loading : false
  },
  roles            : null,
  loadRolesPending : false,
  grid             : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    filterModel         : {}
  },
  add : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  edit : {
    isEditing     : false,
    selected      : null,
    loading       : false,
    error         : null,
    result        : null,
    isCurrentUser : false
  },
  destroy : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  audit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  bulkEdit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  bulkDelete : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    record    : null
  },
  bulkImport : {
    isImporting : false,
    result      : null
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [PRE_INITIALIZE_ADMIN_USERS] : (draft: AdminUsersState, {roles}) => {
    draft.isPreInitialized = true;
    draft.roles = roles;
    draft.loadRolesPending = false;
  },
  [RE_INITIALIZE_ADMIN_USERS] : (draft: AdminUsersState) => {
    reInitializeState(draft, {
      initialState,
      states : [
        'isPreInitialized',
        'roles',
        'loadRolesPending'
      ]
    });
  },
  [INITIALIZE_ADMIN_USERS] : (draft: AdminUsersState) => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [ADMIN_USERS_LOAD_ROLES] : (draft: AdminUsersState) => {
    draft.loadRolesPending = true;
  },
  [SET_GRID_COLUMNS] : ({grid, users}: AdminUsersState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : users.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [SET_ADMIN_USERS_GRID_FILTER_MODEL] : ({grid}: AdminUsersState, {model}) => {
    grid.filterModel = model;
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: AdminUsersState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [ADMIN_USERS_GRID_REFRESHED] : ({add, edit, destroy, bulkDelete, bulkEdit, bulkImport}: AdminUsersState) => {
    add.result = null;
    edit.result = null;
    destroy.result = null;
    bulkDelete.result = null;
    bulkEdit.result = null;
    bulkImport.result = null;
  },

  [GET_ADMIN_USERS_REQUEST] : ({users}: AdminUsersState) => {
    users.loading = true;
  },
  [GET_ADMIN_USERS_RESPONSE] : ({users}: AdminUsersState) => {
    users.loading = false;
  },
  [GET_ADMIN_USERS_ERROR] : (draft: AdminUsersState, {error}: AdminUsersAction) => {
    const {users} = draft;
    users.loading = false;
    users.error = error;
    draft.loadRolesPending = false;
  },

  [IMPORT_ADMIN_USERS] : ({bulkImport}: AdminUsersState, {show, result}: AdminUsersAction) => {
    bulkImport.isImporting = show;
    bulkImport.result = result;
  },

  [ADD_ADMIN_USER] : ({add}: AdminUsersState, {show}: AdminUsersAction) => {
    add.isEditing = show;
    add.result = null;
  },
  [ADD_ADMIN_USER_REQUEST] : ({add}: AdminUsersState) => {
    add.loading = true;
    add.error = add.result = null;
  },
  [ADD_ADMIN_USER_RESPONSE] : ({add}: AdminUsersState, {user}: AdminUsersAction) => {
    add.loading = false;
    add.isEditing = false;
    add.result = user;
  },
  [ADD_ADMIN_USER_ERROR] : ({add}: AdminUsersState, {error}: AdminUsersAction) => {
    add.loading = false;
    add.error = error;
  },

  [EDIT_ADMIN_USER] : ({edit}: AdminUsersState, {user, show, isCurrentUser}: AdminUsersEditAction) => {
    edit.selected      = show ? user : null;
    edit.isEditing     = show;
    edit.isCurrentUser = isCurrentUser;
    edit.result        = null;
  },
  [EDIT_ADMIN_USER_REQUEST] : ({edit}: AdminUsersState) => {
    edit.loading = true;
    edit.error = edit.result = null;
  },
  [EDIT_ADMIN_USER_RESPONSE] : ({edit}: AdminUsersState, {user}: AdminUsersAction) => {
    edit.loading = false;
    edit.isEditing = false;
    edit.result = user;
  },
  [EDIT_ADMIN_USER_ERROR] : ({edit}: AdminUsersState, {error}: AdminUsersAction) => {
    edit.loading = false;
    edit.error = error;
  },

  [DELETE_ADMIN_USER] : ({destroy}: AdminUsersState, {user, show}: AdminUsersAction) => {
    destroy.selected = show ? user : null;
    destroy.isEditing = show;
    destroy.result = null;
  },
  [DELETE_ADMIN_USER_REQUEST] : ({destroy}: AdminUsersState) => {
    destroy.loading = true;
    destroy.error = destroy.result = null;
  },
  [DELETE_ADMIN_USER_RESPONSE] : ({destroy}: AdminUsersState, {user}: AdminUsersAction) => {
    destroy.loading = false;
    destroy.isEditing = false;
    destroy.result = user;
  },
  [DELETE_ADMIN_USER_ERROR] : ({destroy}: AdminUsersState, {error}: AdminUsersAction) => {
    destroy.loading = false;
    destroy.error = error;
    destroy.result = null;
  },

  [AUDIT_ADMIN_USER] : ({audit}: AdminUsersState, {user, show}: AdminUsersAction) => {
    audit.selected = show ? user : null;
    audit.isEditing = show;
    audit.result = null;
  },
  [AUDIT_ADMIN_USER_REQUEST] : ({audit}: AdminUsersState) => {
    audit.loading = true;
    audit.error = audit.result = null;
  },
  [AUDIT_ADMIN_USER_RESPONSE] : ({audit}: AdminUsersState, {result}: AdminUsersAction) => {
    audit.loading = false;
    audit.isEditing = false;
    audit.result = result;
  },
  [AUDIT_ADMIN_USER_ERROR] : ({audit}: AdminUsersState, {error}: AdminUsersAction) => {
    audit.loading = false;
    audit.error = error;
  },

  [EDIT_ADMIN_USERS] : ({bulkEdit}: AdminUsersState, {users, show}: AdminUsersAction) => {
    bulkEdit.selected = show ? users : null;
    bulkEdit.isEditing = show;
    bulkEdit.result = null;
  },
  [EDIT_ADMIN_USERS_REQUEST] : ({bulkEdit}: AdminUsersState) => {
    bulkEdit.loading = true;
    bulkEdit.error = bulkEdit.result = null;
  },
  [EDIT_ADMIN_USERS_RESPONSE] : ({bulkEdit}: AdminUsersState, {users}: AdminUsersAction) => {
    bulkEdit.loading = false;
    bulkEdit.isEditing = false;
    bulkEdit.result = users;
  },
  [EDIT_ADMIN_USERS_ERROR] : ({bulkEdit}: AdminUsersState, {error}: AdminUsersAction) => {
    bulkEdit.loading = false;
    bulkEdit.error = error;
  },

  [DELETE_ADMIN_USERS] : ({bulkDelete}: AdminUsersState, {users, show}: AdminUsersAction) => {
    bulkDelete.selected = show ? users : null;
    bulkDelete.isEditing = show;
    bulkDelete.result = null;
    bulkDelete.record = null;
  },
  [DELETE_ADMIN_USERS_REQUEST] : ({bulkDelete}: AdminUsersState) => {
    bulkDelete.loading = true;
    bulkDelete.error = bulkDelete.result = bulkDelete.record = null;
  },
  [DELETE_ADMIN_USERS_RESPONSE] : ({bulkDelete}: AdminUsersState, {users}: AdminUsersAction) => {
    bulkDelete.loading = false;
    bulkDelete.isEditing = false;
    bulkDelete.result = users;
    bulkDelete.record = users;
  },
  [DELETE_ADMIN_USERS_ERROR] : ({bulkDelete}: AdminUsersState, {error}: AdminUsersAction) => {
    bulkDelete.loading = false;
    bulkDelete.error = error;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: AdminUsersState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
